import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { ChevronDown, ChevronLeft, ChevronRight, LogOut, X } from 'lucide-react'
import { useAuth } from '../auth'
import { useTheme } from '../theme/ThemeProvider'
import { MENUS, DASHBOARD_ITEM, DEFAULT_OPEN, PRE_LICENCIADO_MENU } from '../routes'
import { useVersaoSistema } from '../lib/versao'
import HistoricoVersoesModal from './HistoricoVersoesModal'

// Logo creme sobre o sidebar escuro (igual ao original). No tema Claro o menu fica claro,
// então usamos a arte original (sem inversão) para o logo aparecer sobre o fundo branco.
const LOGO_FILTER = 'brightness(0) saturate(100%) invert(96%) sepia(8%) saturate(200%) hue-rotate(350deg) brightness(105%)'

function NavItem({ item, collapsed, onNavigate, dark }) {
  const Icon = item.icon
  const iconInactive = dark
    ? 'text-neutral-400 group-hover:text-accent-300 flex-shrink-0'
    : 'text-gray-400 group-hover:text-accent-500 flex-shrink-0'
  const inactive = dark
    ? 'text-neutral-300 hover:bg-white/5 hover:text-white border-l-[3px] border-transparent'
    : 'text-gray-600 hover:bg-primary-50 hover:text-graphite border-l-[3px] border-transparent'
  const active = dark
    ? 'bg-gradient-to-r from-primary-500/25 via-accent-500/15 to-transparent text-white border-l-[3px] border-accent-500 font-semibold'
    : 'bg-accent-50 text-accent-700 border-l-[3px] border-accent-500 font-semibold'

  // Item de AÇÃO (ex.: abrir o chat da IA) — não navega, dispara um evento.
  if (item.action === 'ia') {
    return (
      <button type="button" title={collapsed ? item.label : undefined}
        onClick={() => { window.dispatchEvent(new Event('tc-ia-open')); onNavigate && onNavigate() }}
        className={`relative w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-all group ${inactive} ${collapsed ? 'justify-center px-0' : ''}`}>
        <Icon size={18} className={iconInactive} />
        {!collapsed && <span className="truncate flex-1 text-left">{item.label}</span>}
        {!collapsed && item.badge && (
          <span className="text-[9px] font-bold uppercase bg-accent-500 text-white px-1.5 py-0.5 rounded-full leading-none">{item.badge}</span>
        )}
      </button>
    )
  }
  return (
    <NavLink
      to={item.path}
      onClick={onNavigate}
      className={({ isActive }) =>
        `relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-all group ${isActive ? active : inactive} ${collapsed ? 'justify-center px-0' : ''}`
      }
      title={collapsed ? item.label : undefined}
    >
      {({ isActive }) => (
        <>
          <Icon size={18} className={isActive ? (dark ? 'text-accent-400 flex-shrink-0' : 'text-accent-600 flex-shrink-0') : iconInactive} />
          {!collapsed && <span className="truncate flex-1">{item.label}</span>}
          {!collapsed && item.badge && (
            <span className="text-[9px] font-bold uppercase bg-accent-500 text-white px-1.5 py-0.5 rounded-full leading-none">{item.badge}</span>
          )}
        </>
      )}
    </NavLink>
  )
}

// Itens que somem quando a Loja de Produtos Físicos está DESABILITADA (produto físico puro).
// '/meu-link' NÃO entra aqui: a página tem também links de SERVIÇO (Recrutamento/Simulador/
// Indicar Cliente) que continuam disponíveis — o conteúdo de loja é gateado dentro dela.
const LOJA_PATHS = ['/registros/pedidos', '/vendas/painel']

function SidebarInner({ collapsed, onNavigate, dark }) {
  const { user, logout, preLicenciado, lojaHabilitada } = useAuth()
  const versao = useVersaoSistema()
  const [histAberto, setHistAberto] = useState(false)
  const perfil = user?.perfil || 'admin'
  // Pré-licenciado (licença pendente): menu enxuto só com o permitido (PRD)
  let menu = preLicenciado ? PRE_LICENCIADO_MENU : (MENUS[perfil] || MENUS.admin)
  // Loja desligada globalmente (PRD 'Ajuste final'): oculta itens de Loja p/ não-admin
  if (perfil !== 'admin' && !lojaHabilitada) {
    menu = menu.map((sec) => ({ ...sec, items: sec.items.filter((it) => !LOJA_PATHS.includes(it.path)) }))
                .filter((sec) => sec.items.length > 0)
  }
  const [open, setOpen] = useState(preLicenciado ? ['Minha Licença', 'Conheça a Plataforma', 'Apoio'] : DEFAULT_OPEN)
  const loc = useLocation()
  const toggle = (s) => setOpen((o) => (o.includes(s) ? o.filter((x) => x !== s) : [...o, s]))

  const divisor = dark ? 'border-white/5' : 'border-gray-100'
  const divisorBg = dark ? 'bg-white/5' : 'bg-gray-100'
  const secInactive = dark ? 'text-neutral-500 hover:text-neutral-300' : 'text-gray-400 hover:text-gray-600'
  const secActive = dark ? 'text-accent-400' : 'text-accent-600'
  const sairBtn = dark
    ? 'text-neutral-300 hover:bg-accent-500/10 hover:text-accent-400'
    : 'text-gray-600 hover:bg-accent-50 hover:text-accent-700'

  return (
    <>
      {/* Logo */}
      <div className={`flex items-center gap-2 px-4 h-16 flex-shrink-0 border-b ${divisor} ${collapsed ? 'justify-center px-0' : ''}`}>
        <img src="/logothe.png" alt="The Conect" className={collapsed ? 'h-7' : 'h-8'} style={{ filter: dark ? LOGO_FILTER : 'none' }} />
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto tc-scroll px-3 py-4 space-y-1">
        <NavItem item={DASHBOARD_ITEM} collapsed={collapsed} onNavigate={onNavigate} dark={dark} />

        {menu.map((sec) => {
          const isOpen = open.includes(sec.section)
          const hasActive = sec.items.some((it) => loc.pathname === it.path)
          return (
            <div key={sec.section} className="pt-2">
              {!collapsed ? (
                <button
                  onClick={() => toggle(sec.section)}
                  className={`w-full flex items-center justify-between px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors ${hasActive ? secActive : secInactive}`}
                >
                  <span>{sec.section}</span>
                  <ChevronDown size={13} className={`transition-transform ${isOpen ? '' : '-rotate-90'}`} />
                </button>
              ) : (
                <div className={`h-px ${divisorBg} mx-2 my-2`} />
              )}
              {(isOpen || collapsed) && (
                <div className="space-y-0.5 mt-0.5">
                  {sec.items.map((it) => <NavItem key={it.path} item={it} collapsed={collapsed} onNavigate={onNavigate} dark={dark} />)}
                </div>
              )}
            </div>
          )
        })}
      </nav>

      {/* Sair */}
      <div className={`p-3 border-t ${divisor} flex-shrink-0`}>
        <button
          onClick={logout}
          className={`flex items-center gap-3 w-full rounded-xl px-3 py-2.5 text-sm transition-all ${sairBtn} ${collapsed ? 'justify-center px-0' : ''}`}
          title="Sair"
        >
          <LogOut size={18} className="flex-shrink-0" />
          {!collapsed && <span>Sair</span>}
        </button>
        {/* Versão publicada — clicar abre o histórico de atualizações (data e hora de cada entrega) */}
        {versao && (
          <button type="button" onClick={() => setHistAberto(true)}
            className={`w-full mt-1 text-[10px] text-center tabular-nums hover:underline underline-offset-2 ${dark ? 'text-neutral-500 hover:text-accent-400' : 'text-gray-400 hover:text-accent-600'}`}
            title={`Publicado em ${versao.rotulo} — clique para ver o que mudou`}>
            {collapsed ? 'v' : `Versão ${versao.rotulo}`}
          </button>
        )}
      </div>
      <HistoricoVersoesModal open={histAberto} onClose={() => setHistAberto(false)} versaoAtual={versao?.rotulo} />
    </>
  )
}

export default function Sidebar({ collapsed, setCollapsed, mobileOpen, onClose }) {
  const { effective } = useTheme()
  const dark = effective !== 'light' // claro = menu claro; escuro/misto = menu escuro
  const asideBg = dark
    ? 'bg-gradient-to-b from-graphite via-neutral-900 to-graphite text-white'
    : 'bg-white text-graphite border-r border-gray-100'
  return (
    <>
      {/* Desktop */}
      <aside
        className={`tc-sidebar fixed left-0 top-0 h-screen ${asideBg} z-40 flex-col shadow-2xl hidden lg:flex transition-[width] duration-300`}
        style={{ width: collapsed ? 68 : 260 }}
      >
        <SidebarInner collapsed={collapsed} dark={dark} />
        <button
          onClick={() => setCollapsed(!collapsed)}
          className={`absolute -right-3 top-20 w-6 h-6 rounded-full bg-accent-500 border-2 ${dark ? 'border-graphite' : 'border-white'} flex items-center justify-center shadow-lg hover:bg-accent-400 transition-colors text-white`}
        >
          {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
        </button>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} />
          <aside className={`tc-sidebar fixed left-0 top-0 h-screen w-[280px] ${asideBg} z-50 flex flex-col shadow-2xl lg:hidden`}>
            <button onClick={onClose} className={`absolute right-3 top-4 ${dark ? 'text-neutral-400 hover:text-white' : 'text-gray-400 hover:text-graphite'}`}><X size={20} /></button>
            <SidebarInner collapsed={false} onNavigate={onClose} dark={dark} />
          </aside>
        </>
      )}
    </>
  )
}
